/**
 * One-tap quarantine / write-off on the batches listed in a Duti AI answer
 * about what's expiring. Same mutation the Inventory → Near-Expiry page runs
 * (routes/near_expiry.py → apply_near_expiry_action), with a confirmation
 * step because both actions change stock.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Loader2, ShieldAlert, Trash2 } from 'lucide-react';

const LABEL = { quarantine: 'Quarantine', write_off: 'Write off' };

export const ExpiryQuickActions = ({ batches }) => {
  const [pending, setPending] = useState(null); // { batch, action }
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState({});

  const confirm = async () => {
    const { batch, action } = pending;
    setBusy(true);
    try {
      await api.post('/inventory/near-expiry/chat-action', {
        product_id: batch.product_id,
        batch_number: batch.batch_number,
        action,
      });
      setDone((prev) => ({ ...prev, [`${batch.product_id}:${batch.batch_number}`]: action }));
      toast.success(action === 'quarantine'
        ? `${batch.product_name} batch ${batch.batch_number} quarantined`
        : `${batch.product_name} batch ${batch.batch_number} written off`);
      setPending(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not apply that action');
    } finally {
      setBusy(false);
    }
  };

  if (!batches?.length) return null;

  return (
    <div className="mt-2 space-y-1.5" data-testid="expiry-quick-actions">
      {batches.map((b) => {
        const key = `${b.product_id}:${b.batch_number}`;
        return (
          <div key={key} className="rounded-md border bg-background p-2 text-xs">
            <p className="font-medium truncate">{b.product_name}</p>
            <p className="text-muted-foreground">
              Batch {b.batch_number || 'n/a'} · qty {b.quantity} · expires {b.expiry_date} · {b.status}
            </p>
            {done[key] ? (
              <p className="mt-1 text-[11px] font-medium text-primary" data-testid={`expiry-action-done-${key}`}>
                {LABEL[done[key]]} applied
              </p>
            ) : (
              <div className="flex gap-1.5 mt-1.5">
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                  onClick={() => setPending({ batch: b, action: 'quarantine' })}
                  data-testid={`expiry-quarantine-btn-${key}`}>
                  <ShieldAlert className="h-3 w-3 mr-1" /> Quarantine
                </Button>
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-red-600 hover:text-red-700"
                  onClick={() => setPending({ batch: b, action: 'write_off' })}
                  data-testid={`expiry-writeoff-btn-${key}`}>
                  <Trash2 className="h-3 w-3 mr-1" /> Write off
                </Button>
              </div>
            )}
          </div>
        );
      })}

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && !busy && setPending(null)}>
        <AlertDialogContent data-testid="expiry-action-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending ? `${LABEL[pending.action]} this batch?` : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending && (pending.action === 'quarantine'
                ? `Batch ${pending.batch.batch_number} of ${pending.batch.product_name} (qty ${pending.batch.quantity}) will be held back from sale. Stock stays on the books.`
                : `Batch ${pending.batch.batch_number} of ${pending.batch.product_name} will be removed from stock (qty ${pending.batch.quantity}) and logged as a near-expiry write-off. This can't be undone from chat.`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="expiry-action-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={busy} data-testid="expiry-action-confirm">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              {pending ? LABEL[pending.action] : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
